import { getToken, deleteToken } from 'firebase/messaging';
import { toast } from 'react-toastify';
import { messaging } from './helpers/firebase/firebaseConfig';
import { saveUserToken } from './store/actions';

// Constants
const TOKEN_STORAGE_KEY = 'firebaseMessagingToken';
const TOKEN_DISPATCHED_FLAG = 'firebaseMessagingTokenDispatched';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second between retries



/**
 * Retrieves or generates a new FCM token with retry logic
 */
const getFCMTokenWithRetry = async (registration, retries = MAX_RETRIES) => {
  for (let i = 0; i < retries; i++) {
    try {
      const currentToken = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (currentToken) {
        return currentToken;
      }

      // If no token received, wait before retrying
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      }
    } catch (err) {
      console.error(`Token retrieval attempt ${i + 1} failed:`, err);
      if (i === retries - 1) throw err;
    }
  }
  throw new Error('Max retries reached without getting token');
};



/**
 * Registers service worker and handles token generation
 */
const getActiveServiceWorkerRegistration = async () => {
  let registration = await navigator.serviceWorker.getRegistration();
  if (!registration) {
    registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  }
  // Wait until the registration is active and a controller is available
  if (!navigator.serviceWorker.controller) {
    await navigator.serviceWorker.ready;
    await new Promise(resolve => {
      if (navigator.serviceWorker.controller) return resolve();
      const onChange = () => {
        navigator.serviceWorker.removeEventListener('controllerchange', onChange);
        resolve();
      };
      navigator.serviceWorker.addEventListener('controllerchange', onChange);
    });
  }
  return registration;
};

export const requestForToken = async (dispatch) => {
  // Check browser support
  if (!('Notification' in window)) {
    toast.error('متصفحك لا يدعم إشعارات سطح المكتب.');
    return;
  }

  // Check existing token
  const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
  const tokenDispatched = localStorage.getItem(TOKEN_DISPATCHED_FLAG);

  if (storedToken && tokenDispatched !== 'true') {
    try {
      await dispatch(saveUserToken(storedToken));
      localStorage.setItem(TOKEN_DISPATCHED_FLAG, 'true');
      return;
    } catch (error) {
      console.error('Error saving stored token:', error);
      // Continue to try getting a new token
    }
  }

  // Request notification permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    toast.error('تم رفض إذن الإشعارات. لن تتمكن من تلقي إشعارات حول طلباتك.');
    return;
  }

  try {
    // Register service worker
    const registration = await getActiveServiceWorkerRegistration();

    const currentToken = await getFCMTokenWithRetry(registration);

    // Save and dispatch the new token
    localStorage.setItem(TOKEN_STORAGE_KEY, currentToken);
    await dispatch(saveUserToken(currentToken));
    localStorage.setItem(TOKEN_DISPATCHED_FLAG, 'true');

  } catch (err) {
    console.error('Error during token setup:', err);
    toast.error('حدث خطأ أثناء إعداد الإشعارات. يرجى تحديث الصفحة والمحاولة مرة أخرى.');
    
    // Clean up if there was an error
    try {
      await deleteToken(messaging);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch (cleanupError) {
      console.error('Error during cleanup:', cleanupError);
    }
  }
};

// Clean up token when tab closes
window.addEventListener('beforeunload', () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
});

// Optional: Add periodic token refresh
const TOKEN_REFRESH_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 days
setInterval(() => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(TOKEN_DISPATCHED_FLAG);
}, TOKEN_REFRESH_INTERVAL);